import React from 'react';

interface DataPoint {
  date: string;
  value: number;
}

interface UsageChartProps {
  data: DataPoint[];
  title: string;
  color?: string;
  maxValue?: number;
}

export function UsageChart({ data, title, color = '#3b82f6', maxValue }: UsageChartProps) {
  if (data.length === 0) {
    return (
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{title}</h3>
        <div className="text-center py-8 text-slate-400">No data available</div>
      </div>
    );
  }

  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  const width = 600;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate points for the line
  const points = data.map((point, index) => {
    const x = (index / (data.length - 1 || 1)) * chartWidth + padding.left;
    const y = height - padding.bottom - (point.value / max) * chartHeight;
    return { x, y, ...point };
  });

  // Create path for line
  const linePath = points
    .map((point, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command} ${point.x} ${point.y}`;
    })
    .join(' ');

  // Create path for area fill
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Show every nth label to avoid crowding
  const labelInterval = Math.ceil(data.length / 7);

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          style={{ minWidth: '400px' }}
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((factor) => {
            const y = height - padding.bottom - factor * chartHeight;
            return (
              <g key={factor}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke="#334155"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
                <text
                  x={padding.left - 5}
                  y={y + 4}
                  textAnchor="end"
                  fill="#94a3b8"
                  fontSize="12"
                >
                  {Math.round(max * factor)}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path
            d={areaPath}
            fill={color}
            opacity="0.1"
          />

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill={color}
                className="hover:r-6 transition-all cursor-pointer"
              >
                <title>{`${formatDate(point.date)}: ${point.value}`}</title>
              </circle>
            </g>
          ))}

          {/* X-axis labels */}
          {points.map((point, index) => {
            if (index % labelInterval === 0 || index === points.length - 1) {
              return (
                <text
                  key={index}
                  x={point.x}
                  y={height - padding.bottom + 20}
                  textAnchor="middle"
                  fill="#94a3b8"
                  fontSize="11"
                >
                  {formatDate(point.date)}
                </text>
              );
            }
            return null;
          })}
        </svg>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-slate-400">
          Total: <span className="font-semibold text-slate-100">{data.reduce((sum, d) => sum + d.value, 0)}</span>
        </div>
        <div className="text-slate-400">
          Avg: <span className="font-semibold text-slate-100">{Math.round(data.reduce((sum, d) => sum + d.value, 0) / data.length)}</span>
        </div>
        <div className="text-slate-400">
          Peak: <span className="font-semibold text-slate-100">{Math.max(...data.map(d => d.value))}</span>
        </div>
      </div>
    </div>
  );
}
